<template>
  <div class="pest-tagger">

    <!-- Tag selezionati -->
    <div v-if="modelValue.length > 0" class="pest-tags">
      <span
        v-for="(pest, i) in modelValue"
        :key="i"
        class="pest-tag"
        :class="pest.eppo_code ? 'pest-tag--db' : 'pest-tag--free'"
        :title="pest.eppo_code ? `EPPO: ${pest.eppo_code} · ${pest.host_class || ''}` : 'Avversità libera'"
      >
        {{ pest.name_it || pest.name }}
        <button type="button" class="pest-tag-remove" @click="remove(i)">×</button>
      </span>
    </div>

    <!-- Input ricerca -->
    <div class="pest-search-wrap">
      <input
        ref="inputEl"
        v-model="query"
        class="pest-search-input"
        type="text"
        placeholder="Cerca avversità o scrivi nome libero..."
        @input="onInput"
        @keydown.enter.prevent="addFree"
        @keydown.escape="closeSuggestions"
      />
      <button
        v-if="query.trim()"
        type="button"
        class="pest-add-free"
        :title="addFreeTitle"
        @click="addFree"
      >
        + Aggiungi
      </button>
    </div>

    <!-- Suggerimenti dal DB -->
    <ul v-if="suggestions.length > 0" class="pest-suggestions">
      <li
        v-for="s in suggestions"
        :key="s.eppo_code"
        class="pest-suggestion"
        @click="addFromDb(s)"
      >
        <span class="pest-suggestion-name">{{ s.name_it || s.name }}</span>
        <span class="pest-suggestion-meta">
          {{ s.name_it ? s.name : '' }}
          <span v-if="s.host_class" class="pest-host-class">{{ s.host_class }}</span>
        </span>
      </li>
    </ul>

    <p v-if="loadingPests" class="pest-loading">Caricamento avversità...</p>
    <p v-if="!loadingPests && dbPests.length === 0 && cropEppoCodes.length > 0" class="pest-hint">
      Nessuna avversità nel catalogo per questa coltura — inserisci il nome manualmente.
    </p>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { api } from '../services/api.js'

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  cropEppoCodes: { type: Array, default: () => [] },
})
const emit = defineEmits(['update:modelValue'])

const query       = ref('')
const dbPests     = ref([])
const loadingPests = ref(false)
const inputEl     = ref(null)

// Carica avversità dal catalogo quando cambiano i codici EPPO delle colture
watch(() => props.cropEppoCodes, async (codes) => {
  if (!codes.length) { dbPests.value = []; return }
  loadingPests.value = true
  try {
    const res = await api.get(`/api/v2/poderi2/pests_for_crops?crops=${codes.join(',')}`)
    if (res.ok) dbPests.value = res.data || []
  } catch { /* silenzioso */ } finally {
    loadingPests.value = false
  }
}, { immediate: true })

// Suggerimenti filtrati per query e non già selezionati
const selectedCodes = computed(() => new Set(props.modelValue.map(p => p.eppo_code).filter(Boolean)))

const suggestions = computed(() => {
  if (!query.value.trim()) return []
  const q = query.value.toLowerCase()
  return dbPests.value.filter(p =>
    !selectedCodes.value.has(p.eppo_code) &&
    ((p.name_it || '').toLowerCase().includes(q) || (p.name || '').toLowerCase().includes(q))
  ).slice(0, 12)
})

const addFreeTitle = computed(() => `Aggiungi "${query.value.trim()}" come avversità libera`)

function onInput() { /* le suggestions sono computed */ }

function addFromDb(pest) {
  if (selectedCodes.value.has(pest.eppo_code)) return
  emit('update:modelValue', [...props.modelValue, {
    name:      pest.name,
    name_it:   pest.name_it || null,
    eppo_code: pest.eppo_code,
    host_class: pest.host_class || null,
  }])
  query.value = ''
}

function addFree() {
  const name = query.value.trim()
  if (!name) return
  // evita duplicati per nome
  if (props.modelValue.some(p => p.name.toLowerCase() === name.toLowerCase())) {
    query.value = ''
    return
  }
  emit('update:modelValue', [...props.modelValue, { name, name_it: null, eppo_code: null, host_class: null }])
  query.value = ''
}

function remove(i) {
  const arr = [...props.modelValue]
  arr.splice(i, 1)
  emit('update:modelValue', arr)
}

function closeSuggestions() {
  query.value = ''
}
</script>

<style scoped>
.pest-tagger {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

/* Tag */
.pest-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.pest-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.55rem;
  border-radius: 100px;
  font-size: 0.82rem;
  font-weight: 500;
  line-height: 1.3;
}

.pest-tag--db {
  background: rgba(220, 38, 38, 0.1);
  color: #991b1b;
  border: 1px solid rgba(220, 38, 38, 0.25);
}

.pest-tag--free {
  background: rgba(107, 114, 128, 0.1);
  color: #374151;
  border: 1px solid rgba(107, 114, 128, 0.2);
}

.pest-tag-remove {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
  color: inherit;
  opacity: 0.6;
}
.pest-tag-remove:hover { opacity: 1; }

/* Input */
.pest-search-wrap {
  display: flex;
  gap: 0.4rem;
  align-items: center;
}

.pest-search-input {
  flex: 1;
  padding: 0.35rem 0.6rem;
  border: 1px solid var(--border-color, #cbd5e0);
  border-radius: 6px;
  font-size: 0.88rem;
  font-family: inherit;
  background: var(--card-bg, #fff);
  color: var(--text-color, #2d3748);
}
.pest-search-input:focus {
  outline: none;
  border-color: var(--color-primary, #4a90d9);
}

.pest-add-free {
  white-space: nowrap;
  padding: 0.3rem 0.65rem;
  font-size: 0.82rem;
  border: 1px solid var(--color-primary, #4a90d9);
  color: var(--color-primary, #4a90d9);
  background: none;
  border-radius: 5px;
  cursor: pointer;
}
.pest-add-free:hover { background: rgba(74,144,217,0.08); }

/* Suggerimenti */
.pest-suggestions {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 6px;
  background: var(--card-bg, #fff);
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

.pest-suggestion {
  padding: 0.4rem 0.7rem;
  cursor: pointer;
  border-bottom: 1px solid var(--border-color, #f0f0f0);
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.pest-suggestion:last-child { border-bottom: none; }
.pest-suggestion:hover { background: rgba(74,144,217,0.07); }

.pest-suggestion-name {
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--text-color, #2d3748);
}

.pest-suggestion-meta {
  font-size: 0.75rem;
  color: var(--text-muted, #718096);
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.pest-host-class {
  font-size: 0.7rem;
  padding: 0 0.35rem;
  background: rgba(220,38,38,0.08);
  color: #991b1b;
  border-radius: 3px;
}

.pest-loading, .pest-hint {
  font-size: 0.8rem;
  color: var(--text-muted, #718096);
  font-style: italic;
  margin: 0;
}
</style>
